<template>
    <div v-if="searchParams">
        <div class="position-fixed d-flex align-items-center justify-content-center preloader-bg"
            v-if="isSending"
        >
            <div class="bg-white shadow p-5 text-center">
                <div class="spinner-border">
                    <span class="sr-only">{{ $t('loading') }}...</span>
                </div>

                <div class="text-lg mt-2">
                    {{ $t('taxi.pages.summary.creating_order') }}
                </div>

                <div class="text-sm mt-3" v-if="hasPayment">
                    {{ $t('taxi.pages.summary.creating_order_redirect') }}
                </div>

                <form :method="paymentFormMethod" :action="paymentUrl" ref="paymentForm">
                    <input type="hidden" :name="field.toUpperCase()" :value="value" v-for="(value, field) in paymentFields" :key="field">
                </form>
            </div>
        </div>

        <progress-header />

        <div class="container mt-4">
            <div class="row">
                <form class="col-12 col-lg-7 mb-2" @submit.prevent="handleSubmit">
                    <div class="card booking-form-card shadow-sm">
                        <div class="card-header">
                            {{ $t('taxi.pages.summary.header') }}
                        </div>

                        <div class="px-4 py-3">
                            <div class="text-blue-600 font-weight-bold">
                                {{ $t('passengers') }}
                            </div>

                            <div class="py-3 border-bottom" v-for="(passenger, idx) in filledPassengers" :key="idx">
                                {{ passenger.first_name }} {{ passenger.last_name }} {{ passenger.phone }}
                            </div>

                            <div class="mt-4 mb-3 text-blue-600 font-weight-bold">
                                {{ $t('purchaser_details') }}
                            </div>

                            <summary-purchaser />

                            <div class="mt-4 mb-3 text-blue-600 font-weight-bold">
                                {{ $t('invoice') }}
                            </div>

                            <div>
                                <label class="m-0">
                                    <input type="checkbox" class="mr-2" :checked="isInvoice"
                                        @change="onIsInvoiceInput"
                                    >

                                    {{ $t('taxi.pages.summary.is_invoice') }}
                                </label>
                            </div>

                            <summary-invoice class="mt-2" v-if="isInvoice" />

                            <div class="mt-4 text-blue-600 font-weight-bold">
                                {{ $t('payment_method') }}
                            </div>

                            <div>
                                <summary-payment-method v-if="hasPayment && !ecardEnabled" />

                                <PaymentsMethod v-if="hasPayment && ecardEnabled" @payment-method-change="handlePaymentMethod" />

                                <div v-if="travel.is_short_time">
                                    {{ $t('taxi.pages.summary.short_time_payment') }}
                                </div>

                                <div v-if="isDelayedPaymentAllowed">
                                    {{ $t('taxi.pages.summary.delayed_payment') }}
                                </div>
                            </div>

                            <div class="mt-4 text-sm">
                                <div class="mb-2">
                                    {{ $t('taxi.pages.summary.consents_text') }}
                                </div>

                                <consents :consents.sync="consents" />
                            </div>
                        </div>
                    </div>

                    <div class="my-4">
                        <button type="submit" class="btn btn-primary px-4">
                            <span v-if="hasPayment">
                                {{ $t('taxi.pages.summary.submit', {
                                    amount: formatPrice(cartTotal * (travel.currency === 'eur' ? eurToPln : 1), 'pln')
                                }) }}
                            </span>

                            <span v-else>
                                {{ $t('taxi.pages.summary.submit_no_payment') }}
                            </span>
                        </button>
                    </div>
                </form>

                <div class="col-12 col-lg-5">
                    <the-cart />

                    <summary-discount-codes />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .preloader-bg {
        background-color: rgba(255, 255, 255, .8);
        height: 100%;
        left: 0;
        top: 0;
        width: 100%;
        z-index: 100;
    }
</style>

<script>
    import { mapGetters } from 'vuex';

    import SummaryPurchaser from './components/Summary/SummaryPurchaser';
    import SummaryInvoice from './components/Summary/SummaryInvoice';
    import SummaryPaymentMethod from './components/Summary/SummaryPaymentMethod';
    import TheCart from './components/Cart/TheCart';
    import ProgressHeader from './components/ProgressHeader';
    import SummaryDiscountCodes from './components/Summary/SummaryDiscountCodes';
    import Consents from '@components/Consents';
    import toastr from 'toastr';
    import {trackFB, trackGA, trackMS} from '@utilities/tracking';
    import PaymentsMethod from '@components/PaymentsMethod';
    import BackofficeRepository from '@repositories/BackofficeRepository';
    import BackofficeMapsRepository from '@repositories/BackofficeMapsRepository';
    import GoogleGeoAddress from '@/pages/BerbusPage/GoogleGeoAddress';
    import * as Sentry from '@sentry/browser';
    import orderRepository from '@repositories/OrderRepository';

    export default {
        components: {
            SummaryDiscountCodes,
            SummaryPurchaser,
            SummaryInvoice,
            SummaryPaymentMethod,
            ProgressHeader,
            TheCart,
            Consents,
            PaymentsMethod,
        },

        data() {
            return {
                isSending: false,

                paymentFormMethod: null,
                paymentUrl: null,
                paymentFields: {},

                markAllConsentsCheckboxValue: false,
                consents: null,
            };
        },

        computed: {
            ...mapGetters({
                searchParams: 'businessTaxi/searchParameters',
                passengers: 'businessTaxi/getPassengers',
                isInvoice: 'businessTaxi/getIsInvoice',
                cartTotal: 'businessTaxi/getCartTotal',
                cartData: 'businessTaxi/getCartData',
                travel: 'businessTaxi/getTravel',
                eurToPln: 'constants/eurToPln',
                addressTypes: 'constants/addressTypes',
                isDelayedPaymentAllowed: 'user/isDelayedPaymentAllowed',
                discount: 'businessTaxi/getDiscount',
                isUserLoggedIn: 'user/isLoggedIn',
            }),
            ecardEnabled() {
                return window.APP.ecard_enabled;
            },

            filledPassengers() {
                return this.passengers.filter((passenger) => {
                    return passenger.first_name || passenger.last_name || passenger.phone;
                });
            },

            hasPayment() {
                if (this.cartTotal === 0) {
                    return false;
                }

                return !this.travel.is_short_time && !this.isDelayedPaymentAllowed;
            },
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 6,
                checkout_option: window.location.pathname,
            });

            if (!this.passengers) {
                this.$router.push({ name: 'business-taxi.passengers' });
            }
        },

        methods: {
            async addBackofficeOrder(cart, currency, vehicle, discount) {
                this.backofficeOrderInProgress = true;
                this.backofficeError = null;

                const adult_count = cart.passenger_quantities[0].quantity;
                let adult_counter = 0;
                const passengers = cart.passengers.map(i => {
                    return {
                        type: adult_counter++<adult_count?'ADULT':'CHILD_BELOW_4',
                        firstName: i.first_name,
                        lastName: i.last_name,
                        phoneNumber: i.phone,
                    };
                });
                passengers[0].email = cart.purchaser_email;
                passengers[0].phoneNumber = cart.purchaser_phone;
                const {addresses: addresses, flightDetails: flightDetails} = BackofficeMapsRepository.mapAddresses(cart.addresses.filter(a => a.type === 'pickup' || a.type === 'destination'));
                const flightNumber = flightDetails.number?flightDetails.number:null;
                const flightTime = flightDetails.time?flightDetails.time:null;

                //create account if user checked it
                // if(cart.create_account){
                //     await BackofficeRepository.registerClientUser({
                //         'email': cart.purchaser_email,
                //         'firstName': cart.purchaser_first_name,
                //         'lastName': cart.purchaser_last_name,
                //         'phoneNumber': cart.purchaser_phone,
                //         'password': cart.password,
                //     }).catch(error => {
                //         let errorMsg;
                //         if(error.graphQLErrors){
                //             errorMsg = error.graphQLErrors[0].extensions.validation?Object.values(error.graphQLErrors[0].extensions.validation)[0][0].message:error.graphQLErrors[0].message;
                //         }
                //         else{
                //             errorMsg = JSON.stringify(error);
                //         }
                //
                //         //skip error message for unique user and try to log-in in next step
                //         if('iam.user.email_not_unique' !== errorMsg){
                //             toastr.error(errorMsg);
                //         }
                //     });
                //
                //     //login to new account
                //     // const loginResult = await BackofficeRepository.login({
                //     //     'username': cart.purchaser_email,
                //     //     'password': cart.password,
                //     // });
                //     // if(loginResult.error){
                //     //     this.backofficeError = {message: loginResult.error};
                //     //     this.backofficeOrderInProgress = false;
                //     //     toastr.error('Unable to login to your account, try again later.');
                //     //     return;
                //     // }
                // }

                //edit user for correct invoice data
                // if(cart.is_invoice){
                //     await BackofficeRepository.editClientUserMe({
                //         'firstName': 'Follow me!',
                //         'lastName': 'Client',
                //         'phoneNumber': '914850422',
                //         'corporateName': cart.invoice.company,
                //         'addressStreet': cart.invoice.address,
                //         'addressPostal': cart.invoice.zip,
                //         'addressCity': cart.invoice.city,
                //         // 'nip': cart.invoice.vat_number,
                //         // 'email': cart.invoice.email,
                //     });
                // }

                const additionalOptions = cart.travel_options.filter(o => !!o.quantity).map(o => ({ id: o.id, count: o.quantity }));

                //concat comments for each address and travel options with headers of each address type
                //order addresses from: pickup, destination, return_pickup, return_destination ASC
                const comments = cart.addresses.sort((a, b) => {
                    return Object.keys(this.addressTypes).indexOf(a.type) - Object.keys(this.addressTypes).indexOf(b.type);
                }).map(i => {
                    const type = i.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    return i.comments ? `${type}: ${i.comments}` : null;
                });
                comments.push(cart.travel_options_comments);

                const rideStops = addresses
                    .sort((a, b) => {return a.type === 'pickup' ? -1 : 1;})
                    .filter(a => a.type === 'destination' || a.type === 'pickup').map((address) => {return BackofficeRepository.mapAddress(address, address.coords.geo, address.type==='pickup'?cart.departure_date:null);});

                const orderValue = {currency: currency.toUpperCase()};
                if(discount){
                    orderValue.voucherId = discount.id;
                }

                //create backoffice order
                const addOrderByClientParams = {
                    generatePayment: true,
                    requireInvoice: cart.is_invoice,
                    passengers: passengers,
                    comment: comments.filter(comment => comment !== null).join(' | '),
                    rides: [{
                        vehicleRequired: BackofficeRepository.mapVehicleTypeEnum(cart.passengers.length),
                        vehiclePreferredId: vehicle.id,
                        additionalOptions: additionalOptions,
                        stops: rideStops,
                        flightNumber: flightNumber?flightNumber:'',
                        flightDate: BackofficeRepository.mapFlightDate(flightTime, cart.departure_date),
                    }],
                    value: orderValue,
                };
                //add return ride if it exists
                if(cart.has_return_travel){
                    const {addresses: returnAddresses, flightDetails: returnFlightDetails} = BackofficeMapsRepository.mapAddresses(cart.addresses.filter(a => a.type === 'return_pickup' || a.type === 'return_destination'));
                    const returnFlightNumber = returnFlightDetails.number?returnFlightDetails.number:null;
                    const returnFlightTime = returnFlightDetails.time?returnFlightDetails.time:null;
                    const returnRideStops = returnAddresses.sort((a, b) => {return a.type === 'return_pickup' ? -1 : 1;}).filter(a => a.type === 'return_destination' || a.type === 'return_pickup').map((address) => {
                        return BackofficeRepository.mapAddress(address, address.coords.geo, address.type==='return_pickup'?cart.return_departure_date:null);
                    });

                    addOrderByClientParams.rides.push({
                        vehicleRequired: BackofficeRepository.mapVehicleTypeEnum(cart.passengers.length),
                        vehiclePreferredId: vehicle.id,
                        additionalOptions: additionalOptions,
                        stops: returnRideStops,
                        flightNumber: returnFlightNumber?returnFlightNumber:'',
                        flightDate: BackofficeRepository.mapFlightDate(returnFlightTime, cart.return_departure_date),
                    });
                }
                const logExtras = {
                    orderParams: JSON.stringify(addOrderByClientParams),
                    cart: JSON.stringify(cart),
                    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    localTime: new Date().toLocaleString('pl-PL', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}),
                };
                await BackofficeRepository.addOrderByClient('BUSINESS_TAXI', addOrderByClientParams, '/business-taxi/confirmation').then(resp => {
                    this.backofficeError = null;
                    this.backofficeOrder = resp.data.addOrderByClient;
                    this.backofficeOrderInProgress = false;
                }).catch(error => {
                    this.backofficeOrder = null;
                    this.backofficeError = error.graphQLErrors;
                    this.backofficeOrderInProgress = false;
                    logExtras.error = error;
                    Sentry.withScope(scope => {
                        scope.setExtras(logExtras);
                        Sentry.captureMessage('BT order ERROR', 'critical');
                    });
                });

                //revert anon client data to default
                // if(cart.is_invoice){
                //     await BackofficeRepository.editClientUserMeAnonDefault();
                // }
                return this.backofficeOrder?this.backofficeOrder:false;
            },

            googleFindAddressGeo(address, zipcode = null) {
                const geocoder = new window.google.maps.Geocoder();
                const componentRestrictions = zipcode?{postalCode: zipcode}:null;

                return new Promise((resolve, reject) => {
                    geocoder.geocode({
                        address,
                        componentRestrictions,
                        language: window.APP.locale,
                    }, (results, status) => {
                        if (status !== 'OK' && status !== 'ZERO_RESULTS') {
                            this.criticalErrors.push(this.$t('berbus.pages.address.errors.geo_response_error'));
                            reject();
                        }
                        resolve(results);
                    });
                });
            },

            fillAllAddresses(cart)
            {
                if (cart.has_return_travel && cart.addresses)
                {
                    let allAddresses = [];
                    cart.addresses.forEach((address) => {
                        allAddresses.push(address);

                        let reverseAddress = Object.assign({}, address);
                        reverseAddress.flight_number = null;

                        if (address.type === this.addressTypes.pickup || address.type === this.addressTypes.destination) {
                            reverseAddress.flight_number = address.flight_number_return;
                        }

                        if (address.type === this.addressTypes.pickup) {

                            reverseAddress.type = this.addressTypes.return_destination;
                        } else {
                            reverseAddress.type = this.addressTypes.return_pickup;

                        }

                        reverseAddress.comments = reverseAddress.return_comments;
                        delete(reverseAddress.return_comments);
                        delete(address.return_comments);

                        allAddresses.push(reverseAddress);
                    });

                    cart.addresses = allAddresses;
                }
            },

            addConsentsToCart(cart) {
                cart.checked_consents = [];

                this.consents.forEach((consent) => {
                    if (consent.checked) {
                        cart.checked_consents.push(consent.id);
                    }
                });
            },

            handlePaymentMethod(paymentMethod) {
                this.$store.dispatch('businessTaxi/setPaymentMethod', paymentMethod);
            },

            async handleSubmit() {
                if (!this.validatePhone()) {
                    return false;
                }
                //redirect to login page if token expired
                if(!await BackofficeRepository.checkUserTokenBeforeOrder(this.isUserLoggedIn)){
                    await this.$store.dispatch('user/unsetUser');
                    toastr.error(this.$t('auth.session_expired'));
                    this.$router.push({ name: 'login.index', query: {redirectTo: this.$route.fullPath} });
                    return;
                }

                this.isSending = true;

                let cart = this.cartData;
                this.fillAllAddresses(cart);
                this.addConsentsToCart(cart);

                //wait for all responses from this.googleFindAddressGeo to locate all addresses` geo coords and validate
                const lookupAddresses = await Promise.all(
                    cart.addresses.map(address =>
                        this.googleFindAddressGeo(`${address.street}, ${address.zip}`)
                            .then(addresses => addresses ? new GoogleGeoAddress(addresses).toObject() : null)
                    )
                );
                //assign results as geo property to each queried address and show a toastr error if any of them failed and return
                cart.addresses.forEach((address, index) => {
                    address.coords = lookupAddresses[index];
                });
                const failedLookups = cart.addresses.filter(i => !i.coords);
                if(failedLookups.length > 0){
                    toastr.error('We can\'t find address: ' + failedLookups[0].street + ', ' + failedLookups[0].zip);
                    this.isSending = false;
                    return;
                }

                //add backoffice order
                this.addBackofficeOrder(cart, this.travel.currency, this.travel.vehicle, this.discount)
                    .then(async (response) => {
                        // this.isSending = false;//@FIXME
                        // return;//@FIXME
                        if(!response){
                            throw Error('No backoffice order!');
                        }
                        if (cart.is_invoice) {
                            await orderRepository.sendInvoiceData(response.id, response.number, cart.invoice);
                        }
                        trackGA('purchase', {
                            'transaction_id': response.number,
                            'value': parseInt(this.cartTotal, 10)/100,
                            'currency': this.travel.currency,
                            'shipping': 0,
                            'items': [
                                {
                                    name: this.travel.start_place + ' - ' + this.travel.end_place,
                                    brand: this.travel.vehicle.brand + ' ' + this.travel.vehicle.name,
                                    category: 'business_taxi',
                                    quantity: cart.passengers.length,
                                    price: parseInt(this.cartTotal, 10)/100,
                                },
                            ],
                        });
                        trackFB('Purchase', {value: parseInt(this.cartTotal, 10)/100, currency: this.travel.currency});
                        trackMS('PRODUCT_PURCHASE', {'ecomm_prodid':response.number,'ecomm_pagetype':'PURCHASE'});

                        if (!response.payment.payment) {
                            window.location = '/go-to-confirmation/bt/' + response.number;
                            return;
                        }

                        this.paymentUrl = response.payment.payment.appUrl;
                        this.paymentFormMethod = 'GET';
                        this.paymentFields = null;

                        this.$nextTick(() => {
                            this.$store.dispatch('businessTaxi/reset');
                            this.$refs.paymentForm.submit();
                        });
                    })
                    .catch((error) => {
                        this.isSending = false;
                        console.error(error);
                        console.error(this.backofficeError);
                        if(this.backofficeError) {
                            this.backofficeError.forEach((error) => {
                                toastr.error(this.$t('validation.' + error.message));
                            });
                        }
                        else{
                            toastr.error(this.$t('order_error'));
                        }
                        //log error with sentry with response details
                        Sentry.withScope(scope => {
                            scope.setExtras({
                                error: error,
                                response: error.response?error.response:this.backofficeError,
                                cart: cart,
                                tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                localTime: new Date().toLocaleString('pl-PL', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}),
                            });
                            Sentry.captureMessage('BT order ERROR', 'critical');
                        });
                    });
            },

            onIsInvoiceInput(event) {
                this.$store.dispatch('businessTaxi/setIsInvoice', event.target.checked);
            },

            validatePhone() {
                let valid = true;

                if (this.cartData.purchaser_phone && this.cartData.purchaser_phone.length < 11) {
                    toastr.error(this.$t('airport_transfers.pages.phone_length_error'));
                    valid = false;
                }

                if (this.cartData.purchaser_phone[0] !== '+') {
                    toastr.error(this.$t('phone_has_to_start_with_plus'));
                    valid = false;
                }

                return valid;
            },
        },
    };
</script>
